import { integer, sqliteTable, text } from "drizzle-orm/sqlite-core";

import { timestamps } from "../columns.helpers";

export type ActivityLevel = {
  score: number;
  label: string;
  sublabel: string;
};

export type ActivityArchetype = "scored_match" | "session_only";
export type ActivitySessionFormat = "singles" | "doubles" | "team" | "group";
export type ActivityCompetitionMode = "casual" | "competitive";
export type ActivityResultSchema =
  | "winner_only"
  | "sets"
  | "team_score"
  | "runs_wickets"
  | "time_trial"
  | "none";
export type ActivityRendererFamily =
  | "court_match"
  | "team_match"
  | "specialty_match"
  | "group_session";
export type ActivityProfileModule =
  | "sessions"
  | "verified_results"
  | "rating"
  | "record"
  | "attendance";

export type ActivitySessionConfig = {
  formats: ActivitySessionFormat[];
  defaultFormat: ActivitySessionFormat;
  supportsCompetition: boolean;
  participantRange: Partial<
    Record<ActivitySessionFormat, { min: number; max: number }>
  >;
  resultSchema?: Exclude<ActivityResultSchema, "none">;
};

export const DEFAULT_ACTIVITY_SESSION_CONFIG: ActivitySessionConfig = {
  formats: ["group"],
  defaultFormat: "group",
  supportsCompetition: false,
  participantRange: { group: { min: 1, max: 50 } },
};

export type ActivitySessionRules = {
  archetype: ActivityArchetype;
  session: {
    joinable: boolean;
    formats: ActivitySessionFormat[];
    defaultFormat: ActivitySessionFormat;
    participantRange: Partial<
      Record<ActivitySessionFormat, { min: number; max: number }>
    >;
    supportsJoinApproval: boolean;
    supportsInviteCode: boolean;
    showsInFeed: boolean;
    showsInDiscover: boolean;
  };
  competition: {
    allowed: boolean;
    modes: ActivityCompetitionMode[];
    defaultMode: ActivityCompetitionMode | null;
  };
  result: {
    allowed: boolean;
    optional: boolean;
    schema: ActivityResultSchema;
    verification: "participants" | "none";
    retroResultAllowed: boolean;
    affectsRatingWhen: "verified_competitive_only" | "never";
  };
  renderer: {
    family: ActivityRendererFamily;
    templateKey: string | null;
    scoreEditorKey: string | null;
    scorecardKey: string | null;
  };
  profile: {
    modules: ActivityProfileModule[];
  };
};

export type ActivityMetadata = {
  levels: readonly ActivityLevel[];
  metricHelper?: string | null;
  extraConfig?: Record<string, unknown>;
  supportedScoringModes?: string[];
  defaultScoringMode?: string;
  sessionConfig: ActivitySessionConfig;
};

export const activity = sqliteTable("activity", {
  id: text("id").primaryKey(),
  name: text("name").notNull(),
  metric: text("metric").notNull(),
  iconKey: text("icon_key").notNull(),
  // Grouping label surfaced in onboarding + admin. Nullable: a runtime
  // CATEGORY_BY_ID supplies the category for the one uncategorized production
  // row until it is assigned through the admin UI.
  category: text("category"),
  metadata: text("metadata", { mode: "json" })
    .notNull()
    .$type<ActivityMetadata>(),
  order: integer("order").notNull(),
  ...timestamps,
});

export type Activity = typeof activity.$inferSelect;
export type ActivityInsert = typeof activity.$inferInsert;

export type ActivityId = Activity["id"];
